import { create } from "zustand";
import type { ChatMessage } from "../api/types";
import { mergeMessages, trimToLast } from "./replay";

/**
 * How many live lines are kept in memory.
 *
 * The tail is a view of recent conversation, not a second copy of the archive -- anything
 * older is a window fetch away, and an unbounded array in a tab left open for a week is a
 * leak rather than a feature.
 */
const LIVE_LIMIT = 500;

/**
 * Whether the push stream is carrying the app right now.
 *
 * `connecting` is distinct from `polling` because the difference is visible: a stream that
 * has not opened yet should not make the UI announce a fallback it may never need.
 */
export type StreamStatus = "connecting" | "streaming" | "polling";

interface ChatStream {
  status: StreamStatus;
  /** Lines that arrived over the stream, oldest first. */
  live: ChatMessage[];
  /**
   * The highest id seen, for two purposes: `Last-Event-ID` on reconnect, and the cursor the
   * polling fallback asks from. Both need the same number, so it is stored once.
   */
  lastId: number | null;
  setStatus: (status: StreamStatus) => void;
  push: (message: ChatMessage) => void;
  pushMany: (messages: readonly ChatMessage[]) => void;
  reset: () => void;
}

export const useChatStreamStore = create<ChatStream>()((set) => ({
  status: "connecting",
  live: [],
  lastId: null,
  setStatus: (status) => set({ status }),
  push: (message) =>
    set((state) => ({
      live: trimToLast(mergeMessages(state.live, [message]), LIVE_LIMIT),
      lastId: Math.max(state.lastId ?? 0, message.id),
    })),
  pushMany: (messages) =>
    set((state) => {
      if (messages.length === 0) return state;
      return {
        live: trimToLast(mergeMessages(state.live, messages), LIVE_LIMIT),
        lastId: messages.reduce((highest, m) => Math.max(highest, m.id), state.lastId ?? 0),
      };
    }),
  reset: () => set({ live: [], lastId: null, status: "connecting" }),
}));
